import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router';

import { AppShell } from './app-shell';
import { VersionTag } from './version-tag';
import { IntroFlowPage } from '../features/intro/intro-flow-page';
import { PersonalityTestPage } from '../features/personality-test/personality-test-page';
import { CoachConfiguratorPage } from '../features/onboarding/coach-configurator-page';
import { CoachChatPage } from '../features/onboarding/coach-chat-page';
import { PushOptInPage } from '../features/onboarding/push-optin-page';
import { DashboardPage } from '../features/dashboard/dashboard-page';
import { WorkoutPage } from '../features/workout/workout-page';
import { AfterglowPage } from '../features/afterglow/afterglow-page';
import { JournalPage } from '../features/journal/journal-page';
import { MoodHistoryPage } from '../features/mood-history/mood-history-page';
import { ProfilePage } from '../features/profile/profile-page';
import { YourPersonalityPage } from '../features/profile-personality/your-personality-page';
import { NotificationsPage } from '../features/profile-notifications/notifications-page';
import { InstallGuidePage } from '../features/install/install-guide-page';
import { SignupGatePage } from '../features/auth/signup-gate-page';
import { SignupPage } from '../features/auth/signup-page';
import { LoginPage } from '../features/auth/login-page';
import { VerifyEmailPage } from '../features/auth/verify-email-page';
import { WelcomePage } from '../features/auth/welcome-page';
import { UpdateAvailableDialog } from '../features/system/update-available-dialog';
import { GlowLogo } from '../shared/glow-logo';
import { useCoachStore } from '../stores/coach.store';
import { useIntroStore } from '../stores/intro.store';
import { usePersonalityStore } from '../stores/personality.store';
import { useBootSyncStore } from '../stores/boot-sync.store';
import { useSession } from '../lib/auth-client';
import { AUTH_REQUIRED } from '../lib/config';
import { useBootSync } from '../hooks/use-boot-sync';
import { useScrollToTop } from '../hooks/use-scroll-to-top';
import { RouteTracker } from '../lib/analytics';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * `/` is just a switch: returning configured users go straight to the
 * dashboard; users who finished intro but haven't been shown the Big Five
 * test get sent there once (then `personality.seen` flips and they fall
 * through); intro-seen users skip the story and resume coach config; fresh
 * users start at the intro.
 *
 * Auth is opt-in — users can use the whole app without a session. Signup +
 * login are reachable via the Profile page for cross-device sync.
 */
function RootRedirect() {
  const isConfigured = useCoachStore((s) => s.isConfigured);
  const introCompleted = useIntroStore((s) => s.completed);
  const personalitySeen = usePersonalityStore((s) => s.seen);
  const session = useSession();
  const bootStatus = useBootSyncStore((s) => s.status);

  // When a session exists (or is still being determined), wait for boot-sync to
  // hydrate the stores from /me before deciding the route. Otherwise a fresh
  // browser opening the verify link — empty localStorage, config only in the DB
  // — bounces to /intro before /me arrives. The guard is scoped to the
  // has-session case, so the offline/localStorage path routes instantly.
  if (session.isPending || (session.data && bootStatus !== 'settled')) {
    return <BootLoadingScreen />;
  }

  if (isConfigured) {
    // When auth is required, force the gate for signed-out users.
    if (AUTH_REQUIRED && !session.isPending && !session.data) {
      return <Navigate to="/onboarding/signup" replace />;
    }
    // Push opt-in is no longer injected here — it's deferred to the "Begin
    // training" action on the coach chat screen. Configured users land on the
    // dashboard directly.
    return <Navigate to="/dashboard" replace />;
  }
  if (introCompleted && !personalitySeen) {
    return <Navigate to="/personality-test" replace />;
  }
  if (introCompleted) return <Navigate to="/onboarding/coach" replace />;
  return <Navigate to="/intro" replace />;
}

/**
 * Shown at `/` while a signed-in session's boot-sync is still hydrating stores
 * from `/me`. Brand mark on the app background — no app shell, no flash of the
 * wrong route. Deliberately minimal; the sync is usually sub-second.
 */
function BootLoadingScreen() {
  return (
    <div className="flex min-h-[100svh] items-center justify-center bg-background">
      <GlowLogo size={96} />
    </div>
  );
}

function BootSync() {
  useBootSync();
  return null;
}

function ScrollToTopOnRouteChange() {
  const { pathname } = useLocation();
  useScrollToTop(pathname);
  return null;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <UpdateAvailableDialog />
      <BrowserRouter>
        <BootSync />
        <ScrollToTopOnRouteChange />
        <RouteTracker />
        <VersionTag />
        <Routes>
          {/* Pre-auth flow — bare, no app shell. */}
          <Route path="/" element={<RootRedirect />} />
          <Route path="/intro" element={<IntroFlowPage />} />
          <Route path="/personality-test" element={<PersonalityTestPage />} />
          <Route path="/onboarding/coach" element={<CoachConfiguratorPage />} />
          <Route path="/onboarding/notifications" element={<PushOptInPage />} />

          {/* Post-coach signup gate (skippable) + auth pages reachable from Profile. */}
          <Route path="/onboarding/signup" element={<SignupGatePage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/welcome" element={<WelcomePage />} />

          {/* Coach chat lives inside the shell so users can pop into it from
              the bottom nav after onboarding. The shell itself gates on
              coach.isConfigured and redirects to '/' if not yet set up. */}
          <Route element={<AppShell />}>
            <Route path="/onboarding/chat" element={<CoachChatPage />} />
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/workout/:mode" element={<WorkoutPage />} />
            <Route path="/afterglow" element={<AfterglowPage />} />
            <Route path="/journal" element={<JournalPage />} />
            <Route path="/mood-history" element={<MoodHistoryPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/profile/personality" element={<YourPersonalityPage />} />
            <Route path="/profile/notifications" element={<NotificationsPage />} />
            <Route path="/profile/install" element={<InstallGuidePage />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
